/**
 * Feature 1 (#157, 2026-05-28) - InteractionFormModal.
 *
 * Modal form for creating or editing an Agreement Interaction (Note).
 * Two MultiSelect pickers with checkbox renderOption:
 *   - Persons (scoped to alive parties on the parent Agreement)
 *   - Users (Baluarte staff)
 * Plus title, interaction_date, body, is_private, has_other_attendees.
 *
 * On submit:
 *   - Create  -> POST /agreement-interactions (direct, no maker-checker)
 *   - Update  -> PATCH /agreement-interactions/{id} (author-only)
 */
import {
  Button, Checkbox, Group, MultiSelect, Modal, Select, Stack, Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import * as React from 'react';
import { IconCheck, IconSquare } from '@tabler/icons-react';

import { useAgreementParties } from '../hooks/useAgreementParties';
import {
  useCreateAgreementInteraction, useStaffOptions,
  useUpdateAgreementInteraction,
} from '../hooks/useAgreementInteractions';
import type { AgreementInteraction, InteractionKind, UUID } from '../api/types';
import { INTERACTION_KIND_LABELS } from '../api/types';
import { ApiError } from '../api/client';
import { useAlertModal } from './AlertModalProvider';

// 0035 (#41a0): Select options from the single §3j label source.
const KIND_OPTIONS = (Object.keys(INTERACTION_KIND_LABELS) as InteractionKind[])
  .map((k) => ({ value: k, label: INTERACTION_KIND_LABELS[k] }));

interface FormValues {
  interaction_date: string;
  kind: InteractionKind;
  title: string;
  body: string;
  person_ids: string[];
  user_ids: string[];
  is_private: boolean;
  has_other_attendees: boolean;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  agreementId: UUID;
  editing?: AgreementInteraction | null;
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function InteractionFormModal({ opened, onClose, agreementId, editing }: Props) {
  const create = useCreateAgreementInteraction();
  const update = useUpdateAgreementInteraction();
  const { showAlert } = useAlertModal();

  // Task #231 (2026-05-28): person picker options come from the
  // server-resolved person_display_name on each AgreementParty payload.
  // Previously this component fired one /api/persons/<id> per party via
  // useQueries on every modal open — on real-volume agreements that fan-
  // out tripped the DLP rate gate during the v4 incident. Mirror of the
  // KYC contact picker fix in AgreementDetailPage. See
  // _MEMORY/decisions/2026-05-28_deploy-day-incident.md §7.
  const partiesQ = useAgreementParties({ agreement_id: agreementId, limit: 200 });
  const personOptions = React.useMemo(() => {
    const items = partiesQ.data?.items;
    const list = Array.isArray(items) ? items : [];
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const ap of list) {
      if (!ap.person_id || seen.has(ap.person_id)) continue;
      seen.add(ap.person_id);
      options.push({
        value: ap.person_id,
        // Defensive fallback to UUID prefix when the server-resolved
        // name is missing (referenced Person hard-deleted, unusual under
        // FK constraints). Picker remains functional rather than blank.
        label: ap.person_display_name ?? `(${ap.person_id.slice(0, 8)})`,
      });
    }
    return options;
  }, [partiesQ.data]);

  const staffQ = useStaffOptions();
  const userOptions = React.useMemo(
    () => {
      const raw = staffQ.data;
      const list = Array.isArray(raw) ? raw : [];
      return list.map((u) => ({
        value: u.id, label: `${u.display_name} (${u.role})`,
      }));
    },
    [staffQ.data],
  );

  const initial: FormValues = editing ? {
    interaction_date: editing.interaction_date,
    kind: editing.kind,
    title: editing.title,
    body: editing.body ?? '',
    person_ids: editing.person_ids,
    user_ids: editing.user_ids,
    is_private: editing.is_private,
    has_other_attendees: editing.has_other_attendees,
  } : {
    interaction_date: isoToday(),
    kind: 'NOTE',
    title: '',
    body: '',
    person_ids: [],
    user_ids: [],
    is_private: false,
    has_other_attendees: false,
  };

  const form = useForm<FormValues>({
    initialValues: initial,
    validate: {
      interaction_date: (v) => (v ? null : 'Interaction date is required'),
      title: (v) => (v.trim().length > 0 ? null : 'Title is required'),
    },
  });

  React.useEffect(() => {
    if (opened) {
      form.setValues(initial);
      form.resetDirty(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editing?.id]);

  function handleSubmit(values: FormValues) {
    const onError = (err: unknown) => {
      const detail = err instanceof ApiError ? err.detail : String(err);
      showAlert({ color: 'red', title: 'Could not save', message: detail });
    };

    if (editing) {
      update.mutate(
        { id: editing.id, input: {
          interaction_date: values.interaction_date,
          kind: values.kind,
          title: values.title.trim(),
          body: values.body.trim() || null,
          is_private: values.is_private,
          has_other_attendees: values.has_other_attendees,
          person_ids: values.person_ids,
          user_ids: values.user_ids,
        }},
        {
          onSuccess: () => {
            notifications.show({ message: 'Interaction updated', color: 'green' });
            onClose();
          },
          onError,
        },
      );
    } else {
      create.mutate(
        {
          agreement_id: agreementId,
          interaction_date: values.interaction_date,
          kind: values.kind,
          title: values.title.trim(),
          body: values.body.trim() || null,
          is_private: values.is_private,
          has_other_attendees: values.has_other_attendees,
          person_ids: values.person_ids,
          user_ids: values.user_ids,
        },
        {
          onSuccess: () => {
            notifications.show({ message: 'Interaction created', color: 'green' });
            onClose();
          },
          onError,
        },
      );
    }
  }

  const renderOptionWithCheckbox = ({ option, checked }: any) => (
    <Group gap="xs" wrap="nowrap">
      {checked
        ? <IconCheck size={14} color="var(--mantine-color-blue-6)" />
        : <IconSquare size={14} color="var(--mantine-color-gray-5)" />}
      <span>{option.label}</span>
    </Group>
  );

  // CRITICAL — AgreementDetailPage wraps the whole detail in
  // <form id="agreement-form">. Mantine's Modal portals out of the DOM tree
  // but React synthetic events still bubble up the component tree, so without
  // stopPropagation the inner submit fired the outer Agreement form too and
  // produced a phantom "agreements UPDATE" change_request. Fixed 2026-05-28.
  function handleFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.stopPropagation();
    form.onSubmit(handleSubmit)(e);
  }

  return (
    <Modal opened={opened} onClose={onClose}
      title={editing ? 'Edit interaction' : 'New interaction'}
      size="lg" centered>
      <form onSubmit={handleFormSubmit}>
        <Stack>
          <Group grow>
            <TextInput label="Interaction date" type="date" required
              {...form.getInputProps('interaction_date')} />
            <Select label="Kind" data={KIND_OPTIONS} allowDeselect={false}
              {...form.getInputProps('kind')} />
          </Group>

          <Checkbox label="Private (visible only to me, Compliance, Admin)"
            {...form.getInputProps('is_private', { type: 'checkbox' })} />

          <TextInput label="Title" required maxLength={500}
            placeholder="e.g. Quarterly review call with João"
            {...form.getInputProps('title')} />

          <Textarea label="Body / notes" autosize minRows={4} maxRows={20}
            placeholder="Free-text narrative. Mention any external attendees here if you tick the box below."
            {...form.getInputProps('body')} />

          <MultiSelect
            label="Attendees from this Agreement"
            description="Parties currently attached to this Agreement. Scroll to find or type to filter."
            data={personOptions}
            renderOption={renderOptionWithCheckbox}
            comboboxProps={{ withinPortal: true }}
            searchable clearable
            {...form.getInputProps('person_ids')}
          />

          <MultiSelect
            label="Baluarte staff present"
            description="Internal attendees from the firm."
            data={userOptions}
            renderOption={renderOptionWithCheckbox}
            comboboxProps={{ withinPortal: true }}
            searchable clearable
            {...form.getInputProps('user_ids')}
          />

          <Checkbox
            label="Other attendees (described in the body above)"
            description="Tick this if external people not in the pickers were present. Names go in the body."
            {...form.getInputProps('has_other_attendees', { type: 'checkbox' })} />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {editing ? 'Save changes' : 'File interaction'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
